import { useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { api, friendlyError } from "../api/client";
import { Button, ErrorBanner } from "../components/ui";
import { useFocusTrap } from "../lib/focus";

export default function NewCaseDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (caseId: string) => void;
}) {
  const queryClient = useQueryClient();
  const dialog = useRef<HTMLFormElement>(null);
  useFocusTrap(dialog);
  const [cuit, setCuit] = useState("");
  const [legalName, setLegalName] = useState("");
  const [notes, setNotes] = useState("");
  const [error, setError] = useState<{
    message: string;
    detail: string;
  } | null>(null);
  const [saving, setSaving] = useState(false);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const created = await api.createCase(cuit.trim(), legalName.trim(), notes.trim() || null);
      await queryClient.invalidateQueries({ queryKey: ["cases"] });
      onCreated(created.id);
    } catch (e) {
      setError(friendlyError(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 px-6"
      onClick={onClose}
    >
      <form
        ref={dialog}
        onKeyDown={(e) => e.key === "Escape" && onClose()}
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl"
        role="dialog"
        aria-modal="true"
        aria-label="Nuevo legajo"
      >
        <h2 className="text-lg font-semibold">Nuevo legajo</h2>
        <p className="mt-1 text-sm text-muted">
          Se crea el legajo y después se suben los documentos.
        </p>

        <label className="mt-5 block text-sm font-medium" htmlFor="cuit">
          CUIT
        </label>
        <input
          id="cuit"
          value={cuit}
          onChange={(e) => setCuit(e.target.value)}
          placeholder="30-12345678-9"
          className="mt-1 w-full rounded-lg border border-line px-3 py-2 text-sm focus:border-primary focus:outline-none"
        />

        <label className="mt-4 block text-sm font-medium" htmlFor="name">
          Razón social
        </label>
        <input
          id="name"
          value={legalName}
          onChange={(e) => setLegalName(e.target.value)}
          placeholder="Molinos del Sur S.A."
          className="mt-1 w-full rounded-lg border border-line px-3 py-2 text-sm focus:border-primary focus:outline-none"
        />

        <label className="mt-4 block text-sm font-medium" htmlFor="notes">
          Notas internas <span className="font-normal text-muted">(opcional)</span>
        </label>
        <textarea
          id="notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={2}
          className="mt-1 w-full rounded-lg border border-line px-3 py-2 text-sm focus:border-primary focus:outline-none"
        />

        {error ?
          <div className="mt-4">
            <ErrorBanner message={error.message} detail={error.detail} />
          </div>
        : null}

        <div className="mt-6 flex justify-end gap-2">
          <Button type="button" onClick={onClose}>
            Cancelar
          </Button>
          <Button variant="primary" disabled={!cuit.trim() || !legalName.trim() || saving}>
            {saving ? "Creando…" : "Crear legajo"}
          </Button>
        </div>
      </form>
    </div>
  );
}
